import { CLARITY_PROJECT_ID, isClarityProjectConfigured } from '@/config/clarity'
import { canSendClaritySignals } from '@/shared/analytics/clarityCustomEvent'
import { clarity } from 'react-microsoft-clarity'
import type { Session } from '@/api/models/UserModel'

/** SessionStorage key for Clarity user ID so we can identify on next load before session API returns */
export const CLARITY_USER_ID_STORAGE_KEY = 'clarity_user_id'

/** SessionStorage key for Clarity internal user tag (e.g. @artem.earth) so we can set it on restore */
export const CLARITY_INTERNAL_USER_KEY = 'clarity_internal_user'

export type ClarityLoggedInSessionInput = {
  userId: string
  organizationId?: string
  email?: string | null
  /** Used when restoring from sessionStorage before the session API returns */
  internalUserTag?: 'true' | 'false'
}

function resolveInternalUserTag(
  email?: string | null,
  storedTag?: string | null
): 'true' | 'false' {
  if (storedTag === 'true' || storedTag === 'false') {
    return storedTag
  }
  return email?.toLowerCase().endsWith('@artem.earth') ? 'true' : 'false'
}

function persistClarityLoggedInSession(userId: string, internalUserTag: 'true' | 'false'): void {
  sessionStorage.setItem(CLARITY_USER_ID_STORAGE_KEY, userId)
  sessionStorage.setItem(CLARITY_INTERNAL_USER_KEY, internalUserTag)
}

function applyClarityLoggedInSession(input: ClarityLoggedInSessionInput): void {
  if (!canSendClaritySignals()) {
    return
  }

  const internalUserTag = resolveInternalUserTag(
    input.email,
    input.internalUserTag ?? sessionStorage.getItem(CLARITY_INTERNAL_USER_KEY)
  )

  clarity.setTag('internalUser', internalUserTag)
  clarity.identify(
    input.userId,
    input.organizationId ? { organizationId: input.organizationId } : {}
  )
  clarity.consent(true)
}

/** Sync Clarity with an authenticated user (persist, identify, tag, consent). */
export function syncClarityLoggedInSession(input: ClarityLoggedInSessionInput): void {
  if (!isClarityProjectConfigured) {
    return
  }

  const internalUserTag = resolveInternalUserTag(
    input.email,
    input.internalUserTag ?? sessionStorage.getItem(CLARITY_INTERNAL_USER_KEY)
  )
  persistClarityLoggedInSession(input.userId, internalUserTag)
  applyClarityLoggedInSession({ ...input, internalUserTag })
}

/** Restore Clarity state from sessionStorage on app load (before session API returns). */
export function restoreClarityLoggedInSessionFromStorage(): void {
  const userId = sessionStorage.getItem(CLARITY_USER_ID_STORAGE_KEY)
  if (!userId) {
    return
  }

  const storedInternalUser = sessionStorage.getItem(CLARITY_INTERNAL_USER_KEY)
  applyClarityLoggedInSession({
    userId,
    internalUserTag:
      storedInternalUser === 'true' || storedInternalUser === 'false'
        ? storedInternalUser
        : undefined
  })
}

export function syncClarityLoggedInSessionFromApiSession(session: Session): void {
  syncClarityLoggedInSession({
    userId: String(session.id),
    organizationId: session.organization.id,
    email: session.email
  })
}

export function clearClarityLoggedInSessionStorage(): void {
  sessionStorage.removeItem(CLARITY_USER_ID_STORAGE_KEY)
  sessionStorage.removeItem(CLARITY_INTERNAL_USER_KEY)
}

export function initClarityApp(): void {
  if (!isClarityProjectConfigured) {
    return
  }

  clarity.init(CLARITY_PROJECT_ID!)
  restoreClarityLoggedInSessionFromStorage()
}
